import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

interface TipCard {
  icon: string;
  title: string;
  detail: string;
}

interface LifeKnowledgeProps {
  topicIcon: string;
  topic: string;
  tagline: string;
  tips: TipCard[];
  closingText: string;
  themeColor?: string;
}

const DEFAULT_PROPS: LifeKnowledgeProps = {
  topicIcon: '🥗',
  topic: '健康饮食的 5 个误区',
  tagline: '你可能每天都在犯的错误',
  tips: [
    { icon: '❌', title: '不吃碳水就能瘦', detail: '碳水是大脑主要能量来源，长期缺乏会导致注意力下降、代谢降低' },
    { icon: '❌', title: '果汁等于水果', detail: '榨汁后膳食纤维大量流失，糖分浓缩，血糖上升速度是吃水果的 3 倍' },
    { icon: '❌', title: '蛋黄胆固醇高不能吃', detail: '最新研究表明，每天 1-2 个鸡蛋不会增加心血管风险' },
    { icon: '❌', title: '晚上 6 点后不能吃东西', detail: '关键是总热量摄入，而非进食时间；适量晚餐反而有利于睡眠' },
    { icon: '❌', title: '喝骨头汤补钙', detail: '骨头汤含钙量极低（仅 2mg/100ml），更多的是脂肪和嘌呤' },
  ],
  closingText: '科学饮食，拒绝跟风 🧪',
  themeColor: '#f97316',
};

export const LifeKnowledgeTemplate: React.FC<Partial<LifeKnowledgeProps>> = (props) => {
  const p = { ...DEFAULT_PROPS, ...props };
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const theme = p.themeColor!;

  // Opening
  const topicProgress = spring({ frame, fps, config: { damping: 16, stiffness: 100 } });
  const taglineOpacity = interpolate(frame, [20, 45], [0, 1], { extrapolateRight: 'clamp' });

  // Cards
  const cardStart = 60;
  const cardInterval = 65;

  // Closing
  const closingOpacity = interpolate(frame, [cardStart + p.tips.length * cardInterval + 20, cardStart + p.tips.length * cardInterval + 50], [0, 1], { extrapolateRight: 'clamp' });

  // Warm background animation
  const bgShift = interpolate(frame, [0, 600], [0, 30]);

  return (
    <AbsoluteFill
      style={{
        background: `linear-gradient(${160 + bgShift}deg, #fffbeb 0%, #fef3c7 30%, #fde68a 100%)`,
        fontFamily: '-apple-system, "PingFang SC", "Helvetica Neue", sans-serif',
        color: '#1c1917',
        overflow: 'hidden',
      }}
    >
      {/* Decorative shapes */}
      <div
        style={{
          position: 'absolute',
          top: -80,
          right: -60,
          width: 300,
          height: 300,
          borderRadius: '50%',
          background: 'rgba(249, 115, 22, 0.12)',
        }}
      />
      <div
        style={{
          position: 'absolute',
          bottom: 200,
          left: -80,
          width: 250,
          height: 250,
          borderRadius: '50%',
          background: 'rgba(234, 88, 12, 0.08)',
        }}
      />

      {/* Header */}
      <div
        style={{
          position: 'absolute',
          top: 50,
          left: 0,
          right: 0,
          textAlign: 'center',
          transform: `scale(${topicProgress})`,
        }}
      >
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 100,
            height: 100,
            borderRadius: 28,
            background: 'white',
            boxShadow: '0 8px 30px rgba(249, 115, 22, 0.2)',
            fontSize: 56,
            marginBottom: 20,
          }}
        >
          {p.topicIcon}
        </div>
        <h1
          style={{
            fontSize: 64,
            fontWeight: 800,
            margin: '0 60px',
            lineHeight: 1.3,
            color: '#1c1917',
          }}
        >
          {p.topic}
        </h1>
        <p
          style={{
            fontSize: 40,
            marginTop: 12,
            color: theme,
            fontWeight: 600,
            opacity: taglineOpacity,
          }}
        >
          {p.tagline}
        </p>
      </div>

      {/* Tip cards */}
      <div
        style={{
          position: 'absolute',
          top: 400,
          left: 50,
          right: 50,
          display: 'flex',
          flexDirection: 'column',
          gap: 18,
        }}
      >
        {p.tips.map((tip, i) => {
          const startFrame = cardStart + i * cardInterval;
          const cardScale = spring({
            frame: frame - startFrame,
            fps,
            from: 0.85,
            to: 1,
            config: { damping: 14, stiffness: 140, mass: 0.5 },
          });
          const cardOpacity = interpolate(frame, [startFrame, startFrame + 15], [0, 1], { extrapolateRight: 'clamp' });
          const cardY = interpolate(frame, [startFrame, startFrame + 20], [30, 0], { extrapolateRight: 'clamp' });

          // Reveal detail a bit after card appears
          const detailOpacity = interpolate(frame, [startFrame + 15, startFrame + 35], [0, 1], { extrapolateRight: 'clamp' });

          return (
            <div
              key={i}
              style={{
                opacity: cardOpacity,
                transform: `translateY(${cardY}px) scale(${cardScale})`,
              }}
            >
              <div
                style={{
                  background: 'white',
                  borderRadius: 24,
                  padding: '24px 28px',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
                  border: '1px solid rgba(0,0,0,0.04)',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 10 }}>
                  <div
                    style={{
                      width: 44,
                      height: 44,
                      borderRadius: 14,
                      background: `${theme}15`,
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      fontSize: 40,
                      flexShrink: 0,
                    }}
                  >
                    {tip.icon}
                  </div>
                  <span
                    style={{
                      fontSize: 42,
                      fontWeight: 700,
                      color: '#1c1917',
                    }}
                  >
                    {tip.title}
                  </span>
                </div>
                <div
                  style={{
                    fontSize: 36,
                    lineHeight: 1.6,
                    color: '#78716c',
                    paddingLeft: 58,
                    opacity: detailOpacity,
                  }}
                >
                  {tip.detail}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Closing */}
      <div
        style={{
          position: 'absolute',
          bottom: 80,
          left: 0,
          right: 0,
          textAlign: 'center',
          opacity: closingOpacity,
        }}
      >
        <div
          style={{
            display: 'inline-block',
            padding: '16px 40px',
            background: theme,
            borderRadius: 50,
            color: 'white',
            fontSize: 36,
            fontWeight: 700,
            boxShadow: `0 8px 30px ${theme}40`,
          }}
        >
          {p.closingText}
        </div>
      </div>

      {/* Progress */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: `${interpolate(frame, [0, 480], [0, 100], { extrapolateRight: 'clamp' })}%`,
          height: 4,
          background: `linear-gradient(90deg, ${theme}, #ea580c)`,
        }}
      />
    </AbsoluteFill>
  );
};
